// src/components/forms/EvaluationFormBuilder.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Container,
    Paper,
    Box,
    Typography,
    Grid,
    Button,
    TextField,
    Card,
    CardContent,
    CardActions,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    IconButton,
    Switch,
    Alert,
    Chip,
    Divider,
    Tabs,
    Tab,
    Tooltip,
    alpha,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    ArrowBack as BackIcon,
    CloudUpload as UploadIcon,
    Code as CodeIcon,
    Visibility as PreviewIcon,
} from '@mui/icons-material';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import AdminLayout from '../pages/AdminLayout';

export default function EvaluationFormBuilder() {
    const { formId } = useParams();
    const navigate = useNavigate();
    const [isPublished, setIsPublished] = useState(false);
    const [formTitle, setFormTitle] = useState('');
    const [formDescription, setFormDescription] = useState('');
    const [category, setCategory] = useState('general');
    const [passingScore, setPassingScore] = useState(70);
    const [timeLimit, setTimeLimit] = useState(0);
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
    const [jsonInput, setJsonInput] = useState('');
    const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
    const [status, setStatus] = useState('inactive');
    const [tabValue, setTabValue] = useState(0);

    const [newQuestion, setNewQuestion] = useState({
        text: '',
        type: 'single-select',
        mandatory: true,
        maxScore: 10,
        category: 'general',
        difficulty: 'medium',
        hint: '',
        explanation: '',
        options: [{ text: '', score: 5, isCorrect: false }],
    });

    useEffect(() => {
        if (formId && formId !== 'new') {
            fetchForm();
        }
    }, [formId]);

    const fetchForm = async () => {
        try {
            setLoading(true);
            const formDoc = await getDoc(doc(db, 'forms', formId));
            if (formDoc.exists()) {
                const data = formDoc.data();
                setFormTitle(data.title || '');
                setFormDescription(data.description || '');
                setCategory(data.category || 'general');
                setPassingScore(data.passingScore || 70);
                setTimeLimit(data.timeLimit || 0);

                const migratedQuestions = (data.questions || []).map(q => ({
                    ...q,
                    maxScore: q.maxScore || 10,
                    category: q.category || 'general',
                    difficulty: q.difficulty || 'medium',
                    hint: q.hint || '',
                    explanation: q.explanation || '',
                    options: (q.options || []).map(opt => ({
                        text: opt.text || '',
                        score: opt.score || 0,
                        isCorrect: opt.isCorrect || false,
                    }))
                }));

                setQuestions(migratedQuestions);
                setStatus(data.status || 'inactive');
                setIsPublished(data.isPublished ?? false);
            }
        } catch (err) {
            console.error('Error fetching form:', err);
            setError('Failed to load form: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveForm = async () => {
        try {
            setError('');
            setSuccess('');

            if (!formTitle.trim()) {
                setError('Form title is required');
                return;
            }

            if (questions.length === 0) {
                setError('Add at least one question');
                return;
            }

            if (questions.some(q => !q.text.trim())) {
                setError('All questions must have text');
                return;
            }

            if (questions.some(q => q.type !== 'paragraph' && (!q.options || q.options.length === 0))) {
                setError('All questions must have at least one option');
                return;
            }

            setLoading(true);

            const formData = {
                title: formTitle.trim(),
                description: formDescription.trim(),
                formType: 'evaluation',
                category: category,
                passingScore: passingScore,
                timeLimit: timeLimit,
                questions: questions,
                status: status,
                isLocked: false,
                isPublished: isPublished,
                updatedAt: serverTimestamp(),
            };

            if (formId && formId !== 'new') {
                await updateDoc(doc(db, 'forms', formId), formData);
                setSuccess('Form updated successfully!');
            } else {
                const newFormId = `eval_${Date.now()}`;
                await setDoc(doc(db, 'forms', newFormId), {
                    ...formData,
                    createdAt: serverTimestamp(),
                });
                setSuccess('Form created successfully!');
            }

            setTimeout(() => {
                navigate('/admin/forms');
            }, 1500);

        } catch (err) {
            console.error('Error saving form:', err);
            setError('Failed to save form: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleImportJson = () => {
        try {
            const parsed = JSON.parse(jsonInput);

            if (!parsed.title || !Array.isArray(parsed.questions)) {
                setError('Invalid JSON format. Must include title and questions array.');
                return;
            }

            setFormTitle(parsed.title || '');
            setFormDescription(parsed.description || '');
            setCategory(parsed.category || 'general');
            setPassingScore(parsed.passingScore || 70);
            setTimeLimit(parsed.timeLimit || 0);
            setStatus(parsed.status || 'inactive');
            setIsPublished(parsed.isPublished ?? false);

            const importedQuestions = parsed.questions.map(q => ({
                text: q.text || '',
                type: q.type || 'single-select',
                mandatory: q.mandatory ?? true,
                maxScore: q.maxScore || 10,
                category: q.category || 'general',
                difficulty: q.difficulty || 'medium',
                hint: q.hint || '',
                explanation: q.explanation || '',
                options: (q.options || []).map(opt => ({
                    text: opt.text || '',
                    score: opt.score || 0,
                    isCorrect: opt.isCorrect || false,
                })),
            }));

            setQuestions(importedQuestions);
            setJsonDialogOpen(false);
            setSuccess('Form imported successfully!');
            setError('');

        } catch (err) {
            setError('Invalid JSON format: ' + err.message);
        }
    };

    const exportToJson = () => {
        const formJson = {
            title: formTitle,
            description: formDescription,
            formType: 'evaluation',
            category: category,
            passingScore: passingScore,
            timeLimit: timeLimit,
            status: status,
            isPublished: isPublished,
            questions: questions,
        };

        const jsonString = JSON.stringify(formJson, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${formTitle.replace(/\s+/g, '_')}_evaluation.json`;
        link.click();
    };

    const handleAddQuestion = () => {
        setError('');

        if (!newQuestion.text.trim()) {
            setError('Question text is required');
            return;
        }

        if (newQuestion.type !== 'paragraph' && (!newQuestion.options || newQuestion.options.length === 0)) {
            setError('Add at least one option');
            return;
        }

        if (editingQuestionIndex !== null) {
            const updated = [...questions];
            updated[editingQuestionIndex] = newQuestion;
            setQuestions(updated);
        } else {
            setQuestions([...questions, newQuestion]);
        }

        setNewQuestion({
            text: '',
            type: 'single-select',
            mandatory: true,
            maxScore: 10,
            category: 'general',
            difficulty: 'medium',
            hint: '',
            explanation: '',
            options: [{ text: '', score: 5, isCorrect: false }],
        });
        setEditingQuestionIndex(null);
        setDialogOpen(false);
        setError('');
    };

    const handleEditQuestion = (index) => {
        setNewQuestion(questions[index]);
        setEditingQuestionIndex(index);
        setDialogOpen(true);
        setTabValue(0);
    };

    const handleDeleteQuestion = (index) => {
        if (window.confirm('Delete this question?')) {
            setQuestions(questions.filter((_, i) => i !== index));
        }
    };

    const handleAddOption = () => {
        setNewQuestion({
            ...newQuestion,
            options: [...newQuestion.options, { text: '', score: 5, isCorrect: false }],
        });
    };

    const handleOptionChange = (optionIndex, field, value) => {
        const updated = [...newQuestion.options];
        if (field === 'score') {
            updated[optionIndex][field] = parseInt(value) || 0;
        } else if (field === 'isCorrect') {
            updated[optionIndex][field] = value;
        } else {
            updated[optionIndex][field] = value;
        }
        setNewQuestion({ ...newQuestion, options: updated });
    };

    const handleDeleteOption = (optionIndex) => {
        if (newQuestion.options.length <= 1) {
            setError('At least one option is required');
            return;
        }
        setNewQuestion({
            ...newQuestion,
            options: newQuestion.options.filter((_, i) => i !== optionIndex),
        });
    };

    const calculateTotalMaxScore = () => {
        return questions.reduce((sum, q) => sum + (q.maxScore || 10), 0);
    };

    return (
        <AdminLayout title="Form Builder">
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
                    {error && (
                        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
                            {success}
                        </Alert>
                    )}

                    {/* Header */}
                    <Box sx={{ mb: 4 }}>
                        <Button
                            startIcon={<BackIcon />}
                            onClick={() => navigate('/admin/forms')}
                            sx={{ mb: 2 }}
                        >
                            Back to Forms
                        </Button>
                        <Typography variant="h4" sx={{ fontWeight: 600, color: '#003366', mb: 1 }}>
                            ✏️ {formId && formId !== 'new' ? 'Edit' : 'Create'} Evaluation Form
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Create comprehensive evaluation forms with scoring and analytics
                        </Typography>
                    </Box>

                    {/* Form Details */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                            📋 Form Details
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Form Title *"
                                    value={formTitle}
                                    onChange={(e) => setFormTitle(e.target.value)}
                                    placeholder="e.g., Technical Skills Assessment"
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Form Description"
                                    value={formDescription}
                                    onChange={(e) => setFormDescription(e.target.value)}
                                    placeholder="Describe the purpose and scope of this evaluation"
                                    multiline
                                    rows={3}
                                    variant="outlined"
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Category</InputLabel>
                                    <Select
                                        value={category}
                                        onChange={(e) => setCategory(e.target.value)}
                                        label="Category"
                                    >
                                        <MenuItem value="general">General</MenuItem>
                                        <MenuItem value="technical">Technical</MenuItem>
                                        <MenuItem value="behavioral">Behavioral</MenuItem>
                                        <MenuItem value="aptitude">Aptitude</MenuItem>
                                        <MenuItem value="skills">Skills</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Passing Score (%)"
                                    value={passingScore}
                                    onChange={(e) => setPassingScore(parseInt(e.target.value) || 0)}
                                    inputProps={{ min: 0, max: 100 }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Time Limit (minutes)"
                                    value={timeLimit}
                                    onChange={(e) => setTimeLimit(parseInt(e.target.value) || 0)}
                                    placeholder="0 = No limit"
                                    inputProps={{ min: 0 }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                        label="Status"
                                    >
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="inactive">Inactive</MenuItem>
                                        <MenuItem value="draft">Draft</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isPublished}
                                            onChange={(e) => setIsPublished(e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label={isPublished ? '✅ Published (Visible to users)' : '🔒 Unpublished (Hidden from users)'}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/admin/forms')}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSaveForm}
                            disabled={loading}
                            sx={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #5568d3 0%, #6a4291 100%)',
                                },
                            }}
                        >
                            {loading ? 'Saving...' : 'Save Form'}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<CodeIcon />}
                            onClick={() => setJsonDialogOpen(true)}
                            color="secondary"
                        >
                            Import JSON
                        </Button>
                        {questions.length > 0 && (
                            <Button
                                variant="outlined"
                                startIcon={<UploadIcon />}
                                onClick={exportToJson}
                                color="success"
                            >
                                Export JSON
                            </Button>
                        )}
                    </Box>

                    {/* Questions Section */}
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                📝 Questions ({questions.length})
                            </Typography>
                            {questions.length > 0 && (
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Chip
                                        label={`Total Score: ${calculateTotalMaxScore()} points`}
                                        color="primary"
                                        variant="outlined"
                                    />
                                    <Chip
                                        label={`Passing: ${passingScore}%`}
                                        color="success"
                                        variant="outlined"
                                    />
                                </Box>
                            )}
                        </Box>

                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                setNewQuestion({
                                    text: '',
                                    type: 'single-select',
                                    mandatory: true,
                                    maxScore: 10,
                                    category: 'general',
                                    difficulty: 'medium',
                                    hint: '',
                                    explanation: '',
                                    options: [{ text: '', score: 5, isCorrect: false }],
                                });
                                setEditingQuestionIndex(null);
                                setDialogOpen(true);
                                setTabValue(0);
                                setError('');
                            }}
                            sx={{
                                mb: 2,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            }}
                        >
                            Add Question
                        </Button>

                        <Grid container spacing={2}>
                            {questions.map((question, index) => (
                                <Grid item xs={12} key={index}>
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            borderLeft: `4px solid ${question.difficulty === 'easy' ? '#4caf50' :
                                                    question.difficulty === 'hard' ? '#f44336' : '#ff9800'
                                                }`,
                                        }}
                                    >
                                        <CardContent>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                <Typography variant="h6" sx={{ mb: 1, flex: 1 }}>
                                                    {index + 1}. {question.text}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Chip
                                                        label={question.difficulty}
                                                        size="small"
                                                        color={
                                                            question.difficulty === 'easy' ? 'success' :
                                                                question.difficulty === 'hard' ? 'error' : 'warning'
                                                        }
                                                    />
                                                    <Chip
                                                        label={`${question.maxScore} pts`}
                                                        size="small"
                                                        color="primary"
                                                    />
                                                </Box>
                                            </Box>

                                            <Chip
                                                label={question.type}
                                                size="small"
                                                variant="outlined"
                                                sx={{ mb: 1 }}
                                            />

                                            {question.hint && (
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                                                    💡 Hint: {question.hint}
                                                </Typography>
                                            )}

                                            {question.type !== 'paragraph' && (
                                                <>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>
                                                        Options:
                                                    </Typography>
                                                    {question.options?.map((option, optIdx) => (
                                                        <Box key={optIdx} sx={{ display: 'flex', alignItems: 'center', ml: 2, mb: 0.5 }}>
                                                            <Typography variant="body2">
                                                                • {option.text}
                                                                <Chip
                                                                    label={`${option.score} pts`}
                                                                    size="small"
                                                                    sx={{ ml: 1, height: 20 }}
                                                                />
                                                                {option.isCorrect && (
                                                                    <Chip
                                                                        label="✓ Correct"
                                                                        size="small"
                                                                        color="success"
                                                                        sx={{ ml: 1, height: 20 }}
                                                                    />
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </>
                                            )}
                                        </CardContent>
                                        <CardActions>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditQuestion(index)}
                                                color="primary"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteQuestion(index)}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>

                        {questions.length === 0 && (
                            <Paper
                                elevation={0}
                                sx={{
                                    py: 6,
                                    textAlign: 'center',
                                    backgroundColor: alpha('#667eea', 0.05),
                                    border: '2px dashed',
                                    borderColor: alpha('#667eea', 0.2),
                                    borderRadius: 2,
                                }}
                            >
                                <Typography variant="h6" color="text.secondary">
                                    No questions added yet
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Click "Add Question" to start building your evaluation form
                                </Typography>
                            </Paper>
                        )}
                    </Box>

                    {/* Question Dialog */}
                    <Dialog
                        open={dialogOpen}
                        onClose={() => setDialogOpen(false)}
                        maxWidth="md"
                        fullWidth
                    >
                        <DialogTitle>
                            {editingQuestionIndex !== null ? 'Edit Question' : 'Add Question'}
                        </DialogTitle>
                        <DialogContent>
                            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
                                <Tab label="Basic" />
                                <Tab label="Options" />
                                <Tab label="Advanced" />
                            </Tabs>

                            {/* Tab 0: Basic */}
                            {tabValue === 0 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                                    <TextField
                                        fullWidth
                                        label="Question Text *"
                                        value={newQuestion.text}
                                        onChange={(e) => setNewQuestion({ ...newQuestion, text: e.target.value })}
                                        multiline
                                        rows={2}
                                        placeholder="Enter your question here"
                                    />

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Question Type</InputLabel>
                                                <Select
                                                    value={newQuestion.type}
                                                    onChange={(e) => setNewQuestion({ ...newQuestion, type: e.target.value })}
                                                    label="Question Type"
                                                >
                                                    <MenuItem value="single-select">Single Select</MenuItem>
                                                    <MenuItem value="multiple-choice">Multiple Choice</MenuItem>
                                                    <MenuItem value="paragraph">Paragraph</MenuItem>
                                                    <MenuItem value="true-false">True/False</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label="Max Score"
                                                value={newQuestion.maxScore}
                                                onChange={(e) => setNewQuestion({ ...newQuestion, maxScore: parseInt(e.target.value) || 0 })}
                                                inputProps={{ min: 1 }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Category</InputLabel>
                                                <Select
                                                    value={newQuestion.category}
                                                    onChange={(e) => setNewQuestion({ ...newQuestion, category: e.target.value })}
                                                    label="Category"
                                                >
                                                    <MenuItem value="general">General</MenuItem>
                                                    <MenuItem value="technical">Technical</MenuItem>
                                                    <MenuItem value="behavioral">Behavioral</MenuItem>
                                                    <MenuItem value="logical">Logical</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Difficulty</InputLabel>
                                                <Select
                                                    value={newQuestion.difficulty}
                                                    onChange={(e) => setNewQuestion({ ...newQuestion, difficulty: e.target.value })}
                                                    label="Difficulty"
                                                >
                                                    <MenuItem value="easy">Easy</MenuItem>
                                                    <MenuItem value="medium">Medium</MenuItem>
                                                    <MenuItem value="hard">Hard</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={newQuestion.mandatory}
                                                onChange={(e) => setNewQuestion({ ...newQuestion, mandatory: e.target.checked })}
                                            />
                                        }
                                        label="Mandatory Question"
                                    />
                                </Box>
                            )}

                            {/* Tab 1: Options */}
                            {tabValue === 1 && newQuestion.type !== 'paragraph' && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                                        Add options with scores (Higher score = Better answer)
                                    </Typography>
                                    {newQuestion.options?.map((option, idx) => (
                                        <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                                            <TextField
                                                fullWidth
                                                label={`Option ${idx + 1}`}
                                                value={option.text}
                                                onChange={(e) => handleOptionChange(idx, 'text', e.target.value)}
                                                size="small"
                                            />
                                            <TextField
                                                type="number"
                                                label="Score"
                                                value={option.score}
                                                onChange={(e) => handleOptionChange(idx, 'score', e.target.value)}
                                                size="small"
                                                sx={{ width: 100 }}
                                                inputProps={{ min: 0 }}
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={option.isCorrect}
                                                        onChange={(e) => handleOptionChange(idx, 'isCorrect', e.target.checked)}
                                                        size="small"
                                                    />
                                                }
                                                label="✓"
                                            />
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteOption(idx)}
                                                color="error"
                                                disabled={newQuestion.options.length === 1}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    ))}
                                    <Button
                                        onClick={handleAddOption}
                                        fullWidth
                                        startIcon={<AddIcon />}
                                        variant="outlined"
                                    >
                                        Add Option
                                    </Button>
                                </Box>
                            )}

                            {/* Tab 2: Advanced */}
                            {tabValue === 2 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                                    <TextField
                                        fullWidth
                                        label="Hint (Optional)"
                                        value={newQuestion.hint}
                                        onChange={(e) => setNewQuestion({ ...newQuestion, hint: e.target.value })}
                                        placeholder="Provide a hint to help users answer"
                                        multiline
                                        rows={2}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Explanation (Optional)"
                                        value={newQuestion.explanation}
                                        onChange={(e) => setNewQuestion({ ...newQuestion, explanation: e.target.value })}
                                        placeholder="Explain the correct answer (shown after submission)"
                                        multiline
                                        rows={3}
                                    />
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => { setDialogOpen(false); setError(''); }}>
                                Cancel
                            </Button>
                            <Button onClick={handleAddQuestion} variant="contained">
                                {editingQuestionIndex !== null ? 'Update' : 'Add'}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* JSON Import Dialog */}
                    <Dialog
                        open={jsonDialogOpen}
                        onClose={() => setJsonDialogOpen(false)}
                        maxWidth="md"
                        fullWidth
                    >
                        <DialogTitle>Import Form from JSON</DialogTitle>
                        <DialogContent>
                            <TextField
                                fullWidth
                                multiline
                                rows={15}
                                value={jsonInput}
                                onChange={(e) => setJsonInput(e.target.value)}
                                placeholder="Paste your JSON here..."
                                variant="outlined"
                                sx={{ mt: 2, fontFamily: 'monospace' }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                📝 Paste a valid JSON format. See documentation for examples.
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setJsonDialogOpen(false)}>Cancel</Button>
                            <Button onClick={handleImportJson} variant="contained" color="primary">
                                Import
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Paper>
            </Container>
        </AdminLayout>
    );
}
